import { pageTitle } from '@palaccount/config';
import { EINVOICE_MESSAGES, type ComplianceStatus } from '@palaccount/contracts';
import { AlertTriangle, Puzzle, RefreshCw, Send, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Amount, Ltr, Pct } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import {
  useAuthorityAnswer,
  useComplianceConfig,
  useComplianceDashboard,
  useResubmitCompliance,
  useSubmission,
  useSubmissions,
  useSubmitCompliance,
} from './api';
import {
  COUNT_CARDS,
  STATUS_META,
  TAX_CATEGORY_META,
  actionFor,
  breakdownReconciles,
  gateBanner,
  maskToLastFour,
  rejectedCardBorder,
  showsAttention,
  truncateResponse,
} from './compliance';

/**
 * FIN-08 — E-Invoicing & Compliance.
 *
 * §4's layout, followed: the example-jurisdiction banner, then three tabs —
 * Dashboard (attention panel, four count cards, recent submissions),
 * Invoices (a 320-pixel queue beside the detail pane) and Configuration.
 *
 * The prototype defects this screen must NOT reproduce, each fixed here:
 *   - the banner's bold label rendered EMPTY because it read a value the
 *     screen never provided; it reads the adapter's name (§4);
 *   - the sentence after the dash was hard-coded English; it comes from the
 *     dictionary;
 *   - the tax footer promised Net / Tax / Gross and drew two; all three;
 *   - Standard, Zero-Rated and Exempt all rendered grey; three colours;
 *   - a credit note was stored as one and shown as nothing; badge and link;
 *   - every recent row showed the CURRENTLY selected jurisdiction; the row
 *     shows the one it was filed under, which the server stores;
 *   - Resubmit could be drawn without the reason beside it (ruling R1);
 *     here neither is ever drawn without the other.
 *
 * Tab resolution is defensive (§8): an unknown ?tab= lands on Invoices, not
 * on a blank screen.
 */

const TABS = ['dashboard', 'invoices', 'config'] as const;
type Tab = (typeof TABS)[number];

const CHIP = 'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold';

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

function isStatus(value: string | null): value is ComplianceStatus {
  return value !== null && (COUNT_CARDS as readonly string[]).includes(value);
}

export function EinvoicingPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  // §8: "a user whose stored tab no longer resolves lands on Invoices, never
  // on a blank screen."
  const tab: Tab = isTab(params.get('tab')) ? (params.get('tab') as Tab) : 'invoices';
  const rawStatus = params.get('status');
  const statusFilter: ComplianceStatus | undefined = isStatus(rawStatus) ? rawStatus : undefined;
  const selectedId = params.get('submission') ?? undefined;


  const { data: dashboard } = useComplianceDashboard();
  /** NFR-42 — the queue pages; the page rides in the URL beside the filter. */
  const paging = usePagedState();
  const { data: submissionPage } = useSubmissions(statusFilter, paging.page, paging.pageSize);
  const submissions = submissionPage?.submissions;
  const range = usePageRange(submissionPage, paging.pageSize);
  const { data: config } = useComplianceConfig();
  const { data: selected } = useSubmission(selectedId ?? submissions?.[0]?.id);

  const submit = useSubmitCompliance();
  const resubmit = useResubmitCompliance();
  const answer = useAuthorityAnswer();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('einv.title'));
  }, [t]);

  const go = (next: Record<string, string | undefined>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined) merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  };

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (caught) {
      // The cause reaches the decision: a refusal that ARRIVED keeps the
      // server's sentence localized (EINVOICE_MESSAGES are already mapped in
      // `STATIC_MESSAGE_KEYS`), while a request that never came back gets this
      // screen's own sentence rather than the browser's `Failed to fetch`.
      setError(localizeServerCause(t, caught, 'einv.errSaveFailed'));
    }
  };

  const busy = submit.isPending || resubmit.isPending || answer.isPending;

  return (
    <div className="grid gap-4">
      <AccountingTabs />

      {/* §4's banner. The bold label is the ADAPTER's name — the prototype
          read a value it never supplied and drew an empty bold span. */}
      {config?.isExample ? (
        <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <Puzzle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            <strong>
              {t('einv.exampleLabel', {
                name: config.jurisdictionName,
                model: t(`einv.model_${config.submissionModel}`),
              })}
            </strong>{' '}
            — {t('einv.exampleNote')}
          </span>
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold">{t('einv.title')}</h2>
        <nav className="ms-auto flex gap-1" aria-label={t('einv.title')}>
          {TABS.map((entry) => (
            <button
              key={entry}
              type="button"
              onClick={() => go({ tab: entry })}
              className={cn(
                'rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground',
                tab === entry && 'bg-muted text-foreground',
              )}
              aria-current={tab === entry ? 'page' : undefined}
            >
              {t(`einv.tab_${entry}`)}
            </button>
          ))}
        </nav>
      </div>

      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── Dashboard ──────────────────────────────────────────────────── */}
      {tab === 'dashboard' ? (
        <div className="grid gap-4">
          {/* "Needs attention" — appears only when something is rejected,
              and carries the reason AND the field wherever Resubmit is
              offered (ruling R1: neither may be drawn without the other). */}
          {dashboard && showsAttention(dashboard.attention) ? (
            <Card className="border-red-300 dark:border-red-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                  <AlertTriangle className="size-4" aria-hidden /> {t('einv.attention')}
                </CardTitle>
              </CardHeader>
              {/*
               * A LIST, and marked up as one. It is a list of rejected
               * invoices to a screen reader as much as to a designer, and
               * each row is one addressable thing — which is also what lets
               * a journey assert "the reason and the field are on the SAME
               * row as the button" (ruling R1) instead of hunting the page.
               */}
              <CardContent>
                <ul className="grid gap-3">
                  {dashboard.attention.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 dark:border-red-900 dark:bg-red-950/40"
                  >
                    <span className="font-mono text-sm font-semibold">
                      <Ltr value={row.documentCode} />
                    </span>
                    <span className="text-sm">{row.buyerName}</span>
                    <span className="text-sm text-red-800 dark:text-red-300">
                      {row.rejectionReason}
                    </span>
                    {/* §5c field 9 — the offending field, in red fixed-width. */}
                    <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs text-red-900 dark:bg-red-900 dark:text-red-200">
                      <Ltr value={row.rejectedField ?? ''} />
                    </code>
                    <div className="ms-auto flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => go({ tab: 'invoices', submission: row.id, status: undefined })}
                      >
                        {t('einv.open')}
                      </Button>
                      <Gated permission="sales.approve" reasonKey="perm.cannot.submitEinvoices">
                        <Button
                          variant="destructive"
                          disabled={busy}
                          onClick={() => void run(() => resubmit.mutateAsync(row.id))}
                        >
                          <RefreshCw aria-hidden /> {t('einv.resubmit')}
                        </Button>
                      </Gated>
                    </div>
                  </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}

          {/* §8's four count cards, each clickable through to a filtered list. */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {COUNT_CARDS.map((status) => {
              const count = dashboard?.counts[status] ?? 0;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => go({ tab: 'invoices', status, submission: undefined })}
                  className={cn(
                    'rounded-xl border bg-card p-4 text-start hover:bg-muted/50',
                    // "The rejected card gains a red border only when its
                    // count is above zero."
                    status === 'rejected' &&
                      rejectedCardBorder(count) &&
                      'border-red-400 dark:border-red-700',
                  )}
                >
                  <div className="text-sm text-muted-foreground">{t(`einv.card_${status}`)}</div>
                  <div className="mt-1 text-2xl font-bold tabular-nums">{count}</div>
                </button>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('einv.recent')}</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard && dashboard.recent.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('einv.colInvoice')}</TableHead>
                      <TableHead>{t('einv.colJurisdiction')}</TableHead>
                      <TableHead>{t('einv.colStatus')}</TableHead>
                      <TableHead>{t('einv.colSubmittedAt')}</TableHead>
                      <TableHead>{t('einv.colResponse')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.recent.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer"
                        onClick={() => go({ tab: 'invoices', submission: row.id })}
                      >
                        <TableCell className="font-mono">
                          <Ltr value={row.documentCode} />
                        </TableCell>
                        {/* The jurisdiction it was FILED under, not the one
                            selected today — the prototype's own defect. */}
                        <TableCell>{row.jurisdiction}</TableCell>
                        <TableCell>
                          <span className={cn(CHIP, STATUS_META[row.status].chip)}>
                            {t(STATUS_META[row.status].labelKey)}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <Ltr value={row.submittedAt?.slice(0, 16).replace('T', ' ') ?? '—'} />
                        </TableCell>
                        <TableCell className="max-w-[24rem] truncate font-mono text-xs text-muted-foreground">
                          {truncateResponse(row.lastResponse)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                // §15 "Empty".
                <p className="py-6 text-center text-sm text-muted-foreground">{t('einv.noRecent')}</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* ── Invoices: the 320px queue beside the detail pane (§8) ───────── */}
      {tab === 'invoices' ? (
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <Card>
            <CardContent className="grid gap-1 p-2">
              <div className="flex flex-wrap gap-1 p-1">
                <button
                  type="button"
                  onClick={() => go({ status: undefined })}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs',
                    statusFilter === undefined ? 'bg-muted font-semibold' : 'text-muted-foreground',
                  )}
                >
                  {t('einv.filterAll')}
                </button>
                {COUNT_CARDS.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => go({ status })}
                    className={cn(
                      'rounded-md px-2 py-1 text-xs',
                      statusFilter === status ? 'bg-muted font-semibold' : 'text-muted-foreground',
                    )}
                  >
                    {t(STATUS_META[status].labelKey)}
                  </button>
                ))}
              </div>
              {(submissions ?? []).map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => go({ submission: row.id })}
                  className={cn(
                    'rounded-md px-3 py-2 text-start hover:bg-muted/60',
                    selected?.id === row.id && 'bg-muted',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">
                      <Ltr value={row.documentCode} />
                    </span>
                    {/* BR-FIN-08-12 — a credit note is recognisable as one.
                        ERR-FIN-08-07's defect row ("nothing is shown,
                        although the link is stored") is this badge plus the
                        Issued-against line in the detail header — both facts
                        drawn, measured in fin08-credit-note-link.test.tsx. */}
                    {row.isCreditNote ? (
                      <span className={cn(CHIP, 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300')}>
                        {t('einv.creditNote')}
                      </span>
                    ) : null}
                    <span className={cn(CHIP, 'ms-auto', STATUS_META[row.status].chip)}>
                      {t(STATUS_META[row.status].labelKey)}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{row.buyerName}</span>
                    <Amount className="ms-auto text-xs" value={row.total} currency={row.currency} />
                  </div>
                </button>
              ))}
              {(submissions ?? []).length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('einv.noSubmissions')}
                </p>
              ) : null}
              {/* NFR-42 — the range, the total, and the two steps. */}
              {(submissionPage?.total ?? 0) > 0 ? (
                <Pager
                  page={paging.page}
                  pages={range.pages}
                  first={range.first}
                  last={range.last}
                  total={range.total}
                  onPage={paging.setPage}
                  label={t('einv.title')}
                />
              ) : null}
            </CardContent>
          </Card>

          {selected ? (
            <div className="grid gap-4">
              {/* The header: chip, credit-note link, and the ONE action the
                  status offers (BR-FIN-08-05). */}
              <Card>
                <CardContent className="grid gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-base font-bold">
                      <Ltr value={selected.documentCode} />
                    </span>
                    <span className={cn(CHIP, STATUS_META[selected.status].chip)}>
                      {t(STATUS_META[selected.status].labelKey)}
                    </span>
                    {selected.isCreditNote && selected.issuedAgainstCode ? (
                      <span className="text-sm text-muted-foreground">
                        {t('einv.issuedAgainst')}{' '}
                        <Ltr value={selected.issuedAgainstCode} />
                      </span>
                    ) : null}
                    <div className="ms-auto flex gap-2">
                      {/* WHICH of submit/resubmit exists is the submission's
                          own state; whether it is usable is the role's. */}
                      {actionFor(selected.status) === 'submit' ? (
                        <Gated permission="sales.approve" reasonKey="perm.cannot.submitEinvoices">
                          <Button
                            disabled={busy}
                            onClick={() => void run(() => submit.mutateAsync(selected.id))}
                          >
                            <Send aria-hidden /> {t('einv.submit')}
                          </Button>
                        </Gated>
                      ) : null}
                      {actionFor(selected.status) === 'resubmit' ? (
                        <Gated permission="sales.approve" reasonKey="perm.cannot.submitEinvoices">
                          <Button
                            variant="destructive"
                            disabled={busy}
                            onClick={() => void run(() => resubmit.mutateAsync(selected.id))}
                          >
                            <RefreshCw aria-hidden /> {t('einv.resubmit')}
                          </Button>
                        </Gated>
                      ) : null}
                      {/* Q-FIN-08-1's asynchronous half, made reachable: the
                          mock authority is asked for the decision it would
                          otherwise push to a webhook. */}
                      {selected.status === 'submitted' ? (
                        <Gated permission="sales.approve" reasonKey="perm.cannot.submitEinvoices">
                          <Button
                            variant="outline"
                            disabled={busy}
                            onClick={() => void run(() => answer.mutateAsync(selected.id))}
                          >
                            {t('einv.fetchAnswer')}
                          </Button>
                        </Gated>
                      ) : null}
                    </div>
                  </div>

                  {/* §5b's banners — at most one, and never a clearance one
                      under a reporting country (BR-FIN-08-04). */}
                  {gateBanner(selected) === 'clearance_blocked' ? (
                    <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                      <ShieldQuestion className="size-4 shrink-0" aria-hidden />
                      {t('einv.bannerBlocked')}
                    </p>
                  ) : null}
                  {gateBanner(selected) === 'cleared' ? (
                    <p className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                      <ShieldCheck className="size-4 shrink-0" aria-hidden />
                      {t('einv.bannerCleared')}
                    </p>
                  ) : null}
                  {/* BR-FIN-08-09 + ruling R1: the reason and the field, shown
                      unchanged, wherever Resubmit is offered. */}
                  {gateBanner(selected) === 'rejected' ? (
                    <p className="flex flex-wrap items-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                      <AlertTriangle className="size-4 shrink-0" aria-hidden />
                      <strong>{t('einv.bannerRejected')}</strong>
                      <span>{selected.rejectionReason}</span>
                      <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs dark:bg-red-900">
                        <Ltr value={selected.rejectedField ?? ''} />
                      </code>
                    </p>
                  ) : null}
                </CardContent>
              </Card>

              {/* The compliance block: QR, unique code, signature, and the
                  generated file shown VERBATIM (§8, §13). */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('einv.complianceDetail')}</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 lg:grid-cols-[160px_1fr]">
                  <div className="grid gap-1">
                    <div className="flex size-[130px] items-center justify-center rounded-md border bg-white p-2">
                      {selected.qrPayload ? (
                        <QrTile payload={selected.qrPayload} />
                      ) : (
                        <span className="text-center text-xs text-muted-foreground">
                          {t('einv.noQr')}
                        </span>
                      )}
                    </div>
                    {/* §5c field 9's caption — the same words, now in the
                        reader's language.

                        THIS SENTENCE IS RENDERED AT A SECOND SITE:
                        `features/sales/compliance-strip.tsx`. It is the only
                        sentence in this census with two homes, and that makes
                        its map entry a COUPLED change: the moment
                        `EINVOICE_MESSAGES.qrPlaceholderNote` gains an entry in
                        `STATIC_MESSAGE_KEYS`, the strip's site stops being a
                        `wrap-and-map` member and becomes a `wrap` one — and
                        `contract-sentence-render.test.ts` asserts the `wrap`
                        list is EMPTY. So the entry and the strip's wrap have
                        to land together, or main goes red naming a file
                        neither change touched. */}
                    <p className="text-xs text-muted-foreground">
                      {localizeServerMessage(t, EINVOICE_MESSAGES.qrPlaceholderNote)}
                    </p>
                  </div>
                  <dl className="grid gap-2 text-sm">
                    <Field label={t('einv.fUuid')} value={selected.uuid} mono />
                    <Field label={t('einv.fAuthorityCode')} value={selected.authorityCode} mono />
                    <Field label={t('einv.fHash')} value={selected.hash} mono />
                    <Field label={t('einv.fFormat')} value={selected.format} mono />
                    <Field
                      label={t('einv.fModel')}
                      value={t(`einv.model_${selected.submissionModel}`)}
                    />
                    <Field label={t('einv.fJurisdiction')} value={selected.jurisdiction} />
                  </dl>
                  <div className="lg:col-span-2">
                    <p className="mb-1 text-sm font-medium">{t('einv.generatedFile')}</p>
                    <pre
                      dir="ltr"
                      className="max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 text-xs"
                    >
                      {selected.signedPayload ?? t('einv.notYetSubmitted')}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              {/* BR-FIN-08-13 — per line: category, rate, net and tax, and a
                  footer that shows all THREE totals. */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('einv.taxBreakdown')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('einv.colDescription')}</TableHead>
                        <TableHead>{t('einv.colRate')}</TableHead>
                        <TableHead>{t('einv.colCategory')}</TableHead>
                        <TableHead className="text-end">{t('einv.colNet')}</TableHead>
                        <TableHead className="text-end">{t('einv.colTax')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.taxBreakdown.lines.map((line) => (
                        <TableRow key={line.lineNo}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell className="font-mono">
                            <Pct value={line.taxRate} />
                          </TableCell>
                          <TableCell>
                            <span className={cn(CHIP, TAX_CATEGORY_META[line.taxCategory].chip)}>
                              {t(TAX_CATEGORY_META[line.taxCategory].labelKey)}
                            </span>
                          </TableCell>
                          <TableCell className="text-end">
                            <Amount value={line.net} currency={selected.currency} />
                          </TableCell>
                          <TableCell className="text-end font-semibold">
                            <Amount value={line.tax} currency={selected.currency} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={3}>{t('einv.totals')}</TableCell>
                        <TableCell className="text-end">
                          <Amount value={selected.taxBreakdown.net} currency={selected.currency} />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={selected.taxBreakdown.tax} currency={selected.currency} />
                        </TableCell>
                      </TableRow>
                      {/* The GROSS the prototype computed and never drew. */}
                      <TableRow>
                        <TableCell colSpan={4}>{t('einv.gross')}</TableCell>
                        <TableCell className="text-end font-bold">
                          <Amount
                            value={selected.taxBreakdown.gross}
                            currency={selected.currency}
                          />
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                  {breakdownReconciles(selected.taxBreakdown) ? null : (
                    <p role="alert" className="mt-2 text-sm text-destructive">
                      {t('einv.breakdownMismatch')}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* §5e — the submission log. Newest first, for ever. */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('einv.log')}</CardTitle>
                  <p className="text-xs text-muted-foreground">{t('einv.logSubtitle')}</p>
                </CardHeader>
                <CardContent>
                  {selected.attempts.length === 0 ? (
                    <p className="py-4 text-sm text-muted-foreground">{t('einv.notYetSubmitted')}</p>
                  ) : (
                    /*
                     * A hook, deliberately. The log's two guarantees —
                     * newest first, and never rewritten (BR-FIN-08-06) — are
                     * statements about ORDER and about rows persisting, so a
                     * test has to address the rows themselves rather than
                     * search the page for text that also appears in a chip,
                     * a count card and a raw response.
                     */
                    <ul className="grid gap-2" data-testid="submission-log">
                      {selected.attempts.map((attempt) => (
                        <li key={attempt.id} className="rounded-md border p-3 text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="w-28 shrink-0 font-mono text-xs">
                              <Ltr value={attempt.occurredAt.slice(0, 16).replace('T', ' ')} />
                            </span>
                            <span className="font-semibold">{t(`einv.action_${attempt.action}`)}</span>
                            <span className="text-muted-foreground">
                              {t(`einv.result_${attempt.result}`)}
                            </span>
                            {attempt.rejectedField ? (
                              <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs text-red-900 dark:bg-red-900 dark:text-red-200">
                                <Ltr value={attempt.rejectedField} />
                              </code>
                            ) : null}
                            <span className="ms-auto text-xs text-muted-foreground">
                              {attempt.actorName ?? t('einv.actorAuthority')}
                            </span>
                          </div>
                          <pre
                            dir="ltr"
                            className="mt-2 overflow-x-auto text-xs text-muted-foreground"
                          >
                            {attempt.rawResponse}
                          </pre>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {t('einv.pickOne')}
              </CardContent>
            </Card>
          )}
        </div>
      ) : null}

      {/* ── Configuration (§8) ─────────────────────────────────────────── */}
      {tab === 'config' && config ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('einv.configJurisdiction')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <Field label={t('einv.fJurisdiction')} value={config.jurisdictionName} />
              {/* BR-FIN-08-10 — the LABEL comes from the adapter. */}
              <Field label={config.taxIdLabel} value={maskToLastFour(config.taxId) || '—'} mono />
              <Field label={t('einv.fFormat')} value={config.format} mono />
              <Field label={t('einv.fModel')} value={t(`einv.model_${config.submissionModel}`)} />
              <p className="text-xs text-muted-foreground">
                {t(`einv.modelHint_${config.submissionModel}`)}
              </p>
              <div>
                <p className="mb-1 font-medium">{t('einv.requiredFields')}</p>
                <ul className="flex flex-wrap gap-1">
                  {config.requiredFields.map((field) => (
                    <li key={field} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      <Ltr value={field} />
                    </li>
                  ))}
                </ul>
              </div>
              {config.implemented ? null : (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                  {/* PARAMETERISED, so no map entry can exist for it: the map
                      matches whole sentences by exact value and this one
                      carries the jurisdiction. Addressed as a dictionary key
                      here, where the argument is known.

                      The argument stays `config.jurisdiction` — the code, not
                      `jurisdictionName` — because that is what this sentence
                      has always named, and swapping it would be a wording
                      change wearing a localisation change's clothes. */}
                  {t('einv.jurisdictionNotImplemented', { jurisdiction: config.jurisdiction })}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('einv.configChange')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              {/* §8: the tab is "editable only with the e-invoicing
                  configuration permission, and read-only otherwise" — which
                  is the §16.6 ladder, so the controls live on the General
                  Settings screen and this says where. */}
              <p className="text-muted-foreground">{t('einv.configWhere')}</p>
              <ul className="grid gap-1 font-mono text-xs">
                <li>invoicing.einvoice_jurisdiction</li>
                <li>invoicing.einvoice_clearance_required</li>
                <li>invoicing.einvoice_tax_id</li>
              </ul>
              {config.canConfigure ? (
                <Button asChild variant="outline" className="justify-self-start">
                  <a href="/settings?group=invoicing">{t('einv.openSettings')}</a>
                </Button>
              ) : (
                <p className="text-muted-foreground">{t('einv.configReadOnly')}</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null;
  mono?: boolean | undefined;
}) {
  return (
    <div className="grid grid-cols-[10rem_1fr] items-baseline gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('break-all', mono && 'font-mono text-xs')}>
        {value === null || value === '' ? '—' : <Ltr value={value} />}
      </dd>
    </div>
  );
}

/**
 * §5c field 9 — a 12×12 tile filled from a repeatable hash of the payload.
 *
 * It is NOT a scannable code and the caption beside it says so; Q-FIN-08-3
 * asks for a real encoder, which is a rendering change over the SAME stored
 * payload (TODO(qr-encoder)). What matters, and what this draws from, is
 * that the payload is the one that was FILED — read off the record, never
 * recomputed (DECISION.md §8.10).
 */
function QrTile({ payload }: { payload: string }) {
  const cells = useMemo(() => {
    let hash = 2166136261;
    const bits: boolean[] = [];
    for (let index = 0; index < 144; index += 1) {
      hash ^= payload.charCodeAt(index % payload.length);
      hash = Math.imul(hash, 16777619) >>> 0;
      bits.push((hash & 0x40) !== 0);
    }
    return bits;
  }, [payload]);

  return (
    <div className="grid size-full grid-cols-12 gap-0" aria-hidden>
      {cells.map((on, index) => (
        <span key={index} className={on ? 'bg-black' : 'bg-white'} />
      ))}
    </div>
  );
}
